import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { t } from '../utils/i18n.js'
import { WordViewDataManager, type WordGroup, type SpellingSystem, type ColumnConfig, type FilterMode } from '../utils/word-view-data-manager.js'
import '../components/word-entries-list.js'
import '@tlenexkoyotl/xamix-element'
// Import Material Web Components
import '@material/web/textfield/filled-text-field.js'
import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import styles from './word-view-page.css?inline'

/** Session storage key for persisting filter state (per tab) */
const STORAGE_KEY = 'word-view-page-filters'

interface StoredFilters {
  columns: ColumnConfig[]
  filter: string
  filterMode: FilterMode
  currentPage: number
}

@customElement('word-view-page')
export class WordViewPage extends LitElement {
  // Data manager instance
  private dataManager = new WordViewDataManager()
  
  // UI state
  @state() private searchInput = '' // Input value (immediate, for visual feedback)
  @state() private searchMode: FilterMode = 'includes'
  @state() private columns: ColumnConfig[] = [
    { languageId: '1', meaningTypeIds: [], spellingSystemIds: [] }, // Default: Nawatl (1) - all meaning types, all systems
    { languageId: '3', meaningTypeIds: [], spellingSystemIds: [] }   // Default: Spanish (3) - all meaning types, all systems
  ]
  @state() private columnDropdownOpen: boolean[] = [] // Track which column language dropdown is open
  @state() private columnMeaningTypeDropdownOpen: boolean[] = [] // Track which column meaning type dropdown is open
  @state() private columnSpellingSystemDropdownOpen: boolean[] = [] // Track which column spelling system dropdown is open
  @state() private loading = true
  @state() private processing = false // Track if data is being processed
  @state() private error = ''
  @state() private currentPage = 1
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

  static styles = css`${unsafeCSS(styles)}`

  async connectedCallback() {
    super.connectedCallback()
    
    // Set up state change callback
    this.dataManager.setStateChangeCallback((state) => {
      this.loading = state.loading
      this.processing = state.processing
      this.error = state.error
      this.requestUpdate()
    })
    
    // Load stored filters from sessionStorage (per tab)
    this.loadStoredFilters()
    
    // Load data using the manager
    await Promise.all([
      this.dataManager.loadSpellings(),
      this.dataManager.loadLanguages(),
      this.dataManager.loadMeaningTypes()
    ])
    
    // Sync state from manager
    this.loading = this.dataManager.getLoading()
    this.processing = this.dataManager.getProcessing()
    this.error = this.dataManager.getError()
    this.currentPage = this.dataManager.getCurrentPage()
    
    // Initialize column dropdown states if not already set
    if (this.columnDropdownOpen.length !== this.columns.length) {
      this.columnDropdownOpen = new Array(this.columns.length).fill(false)
      this.columnMeaningTypeDropdownOpen = new Array(this.columns.length).fill(false)
      this.columnSpellingSystemDropdownOpen = new Array(this.columns.length).fill(false)
    }
    
    // Listen for i18n updates
    window.addEventListener('i18n-updated', () => {
      this.requestUpdate()
    })
    // Listen for clicks outside to close dropdown
    document.addEventListener('click', this.handleClickOutside)
    
    // Schedule column synchronization after loading completes
    if (this.dataManager.getHeadwords().length > 0) {
      this.scheduleColumnSynchronization()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this.handleClickOutside)
    // Clear any pending debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = null
    }
  }

  // Data accessors (delegate to manager) - using getters for template access
  private get filtered(): WordGroup[] {
    return this.dataManager.getFiltered()
  }
  
  private get paginated(): WordGroup[] {
    return this.dataManager.getPaginated()
  }
  
  private get totalPages(): number {
    return this.dataManager.getTotalPages()
  }
  
  private get pageStart(): number {
    return this.dataManager.getPageStart()
  }
  
  private get pageEnd(): number {
    return this.dataManager.getPageEnd()
  }

  private goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page
      this.dataManager.setCurrentPage(page)
      this.saveFilters()
      // Scroll to top of list
      this.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1)
    }
  }

  private nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1)
    }
  }

  /** Load stored filters from sessionStorage (per tab) */
  private loadStoredFilters() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (stored) {
        const filters: StoredFilters = JSON.parse(stored)
        if (filters.filter !== undefined) {
          this.dataManager.setFilter(filters.filter)
          this.searchInput = filters.filter // Sync input with stored filter
        }
        if (filters.filterMode) {
          this.searchMode = filters.filterMode
          this.dataManager.setFilterMode(filters.filterMode)
        }
        if (filters.currentPage) {
          this.currentPage = filters.currentPage
          this.dataManager.setCurrentPage(filters.currentPage)
        }
        if (filters.columns && filters.columns.length > 0) {
          this.columns = filters.columns
        }
      }
    } catch (e) {
      console.warn('Failed to load stored filters from sessionStorage:', e)
      // Continue with default values
    }
  }

  /** Save current filters to sessionStorage (per tab) */
  private saveFilters() {
    try {
      const filters: StoredFilters = {
        columns: this.columns,
        filter: this.dataManager.getFilter(),
        filterMode: this.dataManager.getFilterMode(),
        currentPage: this.currentPage,
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
    } catch (e) {
      console.warn('Failed to save filters to sessionStorage:', e)
      // Continue without saving
    }
  }

  /** Handle search input change with 0.5 second debounce. */
  private handleSearchInput(value: string) {
    // Update input value immediately for visual feedback
    this.searchInput = value
    
    // Clear any existing debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
    
    // Set a new debounce timer to update actual filter after 0.5 seconds of no typing
    // setFilter will trigger loadSpellings which does server-side filtering
    this.searchDebounceTimer = setTimeout(() => {
      this.dataManager.setFilter(this.searchInput)
      this.currentPage = 1
      this.dataManager.setCurrentPage(1)
      this.saveFilters()
      this.requestUpdate()
      this.searchDebounceTimer = null
    }, 500)
  }

  private setSearchMode(mode: FilterMode) {
    if (this.searchMode === mode) return
    this.searchMode = mode
    this.dataManager.setFilterMode(mode)
    this.currentPage = 1
    this.dataManager.setCurrentPage(1)
    this.saveFilters()
    this.requestUpdate()
  }

  /** Toggle meaning type selection for a column */
  private toggleColumnMeaningType(columnIndex: number, meaningTypeId: string) {
    this.columns = this.columns.map((col, idx) => {
      if (idx === columnIndex) {
        if (col.meaningTypeIds.includes(meaningTypeId)) {
          return { ...col, meaningTypeIds: col.meaningTypeIds.filter(id => id !== meaningTypeId) }
        } else {
          return { ...col, meaningTypeIds: [...col.meaningTypeIds, meaningTypeId] }
        }
      }
      return col
    })
    this.currentPage = 1
    this.dataManager.setCurrentPage(1)
    // Clear caches when column configuration changes
    this.dataManager.clearFilterCache()
    this.saveFilters()
    this.requestUpdate()
  }

  /** Toggle column meaning type dropdown */
  private toggleColumnMeaningTypeDropdown(columnIndex: number) {
    this.columnMeaningTypeDropdownOpen = this.columnMeaningTypeDropdownOpen.map((open, idx) =>
      idx === columnIndex ? !open : false
    )
    // Close other dropdowns when opening meaning type dropdown
    this.columnDropdownOpen = this.columnDropdownOpen.map(() => false)
    this.columnSpellingSystemDropdownOpen = this.columnSpellingSystemDropdownOpen.map(() => false)
    this.requestUpdate()
  }

  /** Toggle spelling system selection for a column */
  private toggleColumnSpellingSystem(columnIndex: number, spellingSystemId: string) {
    this.columns = this.columns.map((col, idx) => {
      if (idx === columnIndex) {
        if (col.spellingSystemIds.includes(spellingSystemId)) {
          return { ...col, spellingSystemIds: col.spellingSystemIds.filter(id => id !== spellingSystemId) }
        } else {
          return { ...col, spellingSystemIds: [...col.spellingSystemIds, spellingSystemId] }
        }
      }
      return col
    })
    this.currentPage = 1
    this.dataManager.setCurrentPage(1)
    // Clear caches when column configuration changes
    this.dataManager.clearFilterCache()
    this.saveFilters()
    this.requestUpdate()
  }

  /** Toggle column spelling system dropdown */
  private toggleColumnSpellingSystemDropdown(columnIndex: number) {
    this.columnSpellingSystemDropdownOpen = this.columnSpellingSystemDropdownOpen.map((open: boolean, idx: number) =>
      idx === columnIndex ? !open : false
    )
    // Close other dropdowns when opening spelling system dropdown
    this.columnDropdownOpen = this.columnDropdownOpen.map(() => false)
    this.columnMeaningTypeDropdownOpen = this.columnMeaningTypeDropdownOpen.map(() => false)
    this.requestUpdate()
  }

  /** Get available spelling systems for a language from the spellings data */
  private getAvailableSpellingSystemsForLanguage(languageId: string): SpellingSystem[] {
    return this.dataManager.getAvailableSpellingSystemsForLanguage(languageId)
  }

  /** Close dropdown when clicking outside */
  private handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    
    // Check if click is inside any column dropdown
    let clickedInColumnDropdown = false
    this.columns.forEach((_, colIndex) => {
      const columnDropdown = this.shadowRoot?.querySelector(`[data-column-index="${colIndex}"]`)
      if (columnDropdown && columnDropdown.contains(target)) {
        clickedInColumnDropdown = true
      }
    })
    
    // Close all column dropdowns if click is outside
    if (!clickedInColumnDropdown) {
      this.columnDropdownOpen = this.columnDropdownOpen.map(() => false)
      this.columnMeaningTypeDropdownOpen = this.columnMeaningTypeDropdownOpen.map(() => false)
      this.columnSpellingSystemDropdownOpen = this.columnSpellingSystemDropdownOpen.map(() => false)
    }
  }

  /** Change language for a column */
  private changeColumnLanguage(columnIndex: number, languageId: string) {
    this.columns = this.columns.map((col, idx) =>
      idx === columnIndex ? { ...col, languageId, spellingSystemIds: [] } : col
    )
    this.columnDropdownOpen = this.columnDropdownOpen.map(() => false)
    // Clear caches when column configuration changes
    this.dataManager.clearFilterCache()
    this.requestUpdate()
    this.saveFilters()
  }

  /** Toggle column language dropdown */
  private toggleColumnDropdown(columnIndex: number) {
    this.columnDropdownOpen = this.columnDropdownOpen.map((open, idx) =>
      idx === columnIndex ? !open : false
    )
    // Close other dropdowns when opening language dropdown
    this.columnMeaningTypeDropdownOpen = this.columnMeaningTypeDropdownOpen.map(() => false)
    this.columnSpellingSystemDropdownOpen = this.columnSpellingSystemDropdownOpen.map(() => false)
    this.requestUpdate()
  }

  /** Add a new column */
  private addColumn() {
    // Add the first available language that's not already in a column
    const availableLang = this.dataManager.getLanguages().find(
      lang => !this.columns.some(col => col.languageId === lang.id)
    )
    if (availableLang) {
      this.columns = [...this.columns, { languageId: availableLang.id, meaningTypeIds: [], spellingSystemIds: [] }]
      this.columnDropdownOpen = [...this.columnDropdownOpen, false]
      this.columnMeaningTypeDropdownOpen = [...this.columnMeaningTypeDropdownOpen, false]
      this.columnSpellingSystemDropdownOpen = [...this.columnSpellingSystemDropdownOpen, false]
      // Clear caches when column configuration changes
      this.dataManager.clearFilterCache()
      this.requestUpdate()
      this.saveFilters()
    }
  }

  /** Remove a column */
  private removeColumn(columnIndex: number) {
    if (this.columns.length > 1) {
      this.columns = this.columns.filter((_, idx) => idx !== columnIndex)
      this.columnDropdownOpen = this.columnDropdownOpen.filter((_, idx) => idx !== columnIndex)
      this.columnMeaningTypeDropdownOpen = this.columnMeaningTypeDropdownOpen.filter((_, idx) => idx !== columnIndex)
      this.requestUpdate()
      this.saveFilters()
    }
  }

  firstUpdated(changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(changedProperties)
    // Ensure synchronization happens after first render
    this.scheduleColumnSynchronization()
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties)
    // After render, synchronize column widths across all rows
    // Only sync if we have data and are not loading
    if (!this.loading && this.dataManager.getHeadwords().length > 0) {
      this.scheduleColumnSynchronization()
    }
  }

  private scheduleColumnSynchronization() {
    // Wait for custom elements to be defined (including word-entries-list for shadow DOM access)
    Promise.all([
      customElements.whenDefined('xamix-element').catch(() => {}),
      customElements.whenDefined('md-outlined-button').catch(() => {}),
      customElements.whenDefined('md-filled-button').catch(() => {}),
      customElements.whenDefined('word-entries-list').catch(() => {}),
    ]).then(() => {
      // Use multiple requestAnimationFrame calls to ensure all rendering is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.synchronizeColumnWidths()
          })
        })
      })
    }).catch(() => {
      // If custom elements aren't defined, still try to synchronize
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.synchronizeColumnWidths()
          })
        })
      })
    })
  }

  private synchronizeColumnWidths() {
    // Find all column layouts (header + all entry rows)
    // Note: list rows are inside word-entries-list's shadow DOM, so we must traverse into it
    const scrollContainer = this.shadowRoot?.querySelector('.columns-scroll-container')
    if (!scrollContainer) return

    const allGrids: Element[] = Array.from(scrollContainer.querySelectorAll('.columns-layout'))

    // Also find column layouts inside word-entries-list's shadow DOM (list rows)
    const listEl = scrollContainer.querySelector('word-entries-list')
    if (listEl?.shadowRoot) {
      const listGrids = listEl.shadowRoot.querySelectorAll('.columns-layout')
      allGrids.push(...Array.from(listGrids))
    }

    if (allGrids.length === 0) return

    const totalColumns = this.columns.length + (this.dataManager.getLanguages().some(lang => !this.columns.some(col => col.languageId === lang.id)) ? 1 : 0)
    const columnWidths: number[] = new Array(totalColumns).fill(0)

    // First pass: measure all columns to find maximum width for each column
    allGrids.forEach((grid) => {
      const columns = grid.querySelectorAll('.column')
      columns.forEach((col, index) => {
        if (index < totalColumns) {
          const colEl = col as HTMLElement
          const rect = colEl.getBoundingClientRect()
          if (rect.width > 0 || rect.height > 0) {
            const width = rect.width
            if (width > columnWidths[index]) {
              columnWidths[index] = width
            }
          }
        }
      })
    })

    // If we didn't get valid measurements, try again after a short delay
    if (!columnWidths.some(w => w > 0)) {
      setTimeout(() => {
        this.synchronizeColumnWidths()
      }, 100)
      return
    }

    // Build grid-template-columns string (use first columns.length for list, full for header)
    const gridTemplateColumns = columnWidths.map(w => {
      const minWidth = Math.max(w, 150)
      return `${minWidth}px`
    }).join(' ')

    // Apply to header grid (in word-view-page) - includes Add column if present
    scrollContainer.querySelectorAll('.columns-layout').forEach((grid) => {
      (grid as HTMLElement).style.gridTemplateColumns = gridTemplateColumns
    })

    // Apply to word-entries-list via CSS variable - only data columns (list has no Add column)
    if (listEl) {
      const listColumnWidths = columnWidths.slice(0, this.columns.length).map(w => `${Math.max(w, 150)}px`).join(' ')
      ;(listEl as HTMLElement).style.setProperty('--entries-grid-template-columns', listColumnWidths)
    }
  }

  render() {
    const list = this.paginated
    const showPagination = !this.loading && !this.error && this.filtered.length > 0

    return html`
      <div class="page">
        <div class="search-wrap">
          <md-filled-text-field
            type="search"
            label=${t('wordView.searchPlaceholder')}
            .value=${this.searchInput}
            @input=${(e: Event) => { 
              const target = e.target as any
              this.handleSearchInput(target.value || '')
            }}
            autofocus
            style="flex: 1; width: 100%;"
          ></md-filled-text-field>
          <div class="search-mode-toggle" role="group" aria-label=${t('wordView.searchMode')}>
            <button
              type="button"
              class="search-mode-button ${this.searchMode === 'includes' ? 'active' : ''}"
              @click=${() => this.setSearchMode('includes')}
            >
              ${t('wordView.searchModeIncludes')}
            </button>
            <button
              type="button"
              class="search-mode-button ${this.searchMode === 'equals' ? 'active' : ''}"
              @click=${() => this.setSearchMode('equals')}
            >
              ${t('wordView.searchModeEquals')}
            </button>
          </div>
        </div>

        ${this.loading || this.processing
          ? html`<p class="loading">${this.loading ? t('wordView.loading') : 'Processing data...'}</p>`
          : this.error
            ? html`<p class="error">${this.error}</p>`
            : this.filtered.length === 0 && !this.processing
              ? html`<p class="empty">${t('wordView.noHeadwords')}</p>`
              : html`
                  <p class="list-header">${t('wordView.headwords')}</p>
                  <!-- Scrollable container for header and content rows -->
                  <div class="columns-scroll-container">
                    <!-- Column headers (only shown once at the top) -->
                    <!-- Column widths will be synchronized via JavaScript after render -->
                    <div class="columns-layout columns-header" data-testid="columns-layout" style="grid-template-columns: repeat(${this.columns.length + (this.dataManager.getLanguages().some(lang => !this.columns.some(col => col.languageId === lang.id)) ? 1 : 0)}, max-content); margin-bottom: 0.5rem;">
                    ${this.columns.map((col, colIndex) => {
                      const colLang = this.dataManager.getLanguages().find(l => l.id === col.languageId)
                      return html`
                        <div class="column">
                          <div class="column-header">
                            <div style="display: flex; flex-direction: column; gap: 0.5rem; flex: 1;">
                              <div class="column-language-select" data-column-index="${colIndex}">
                              <md-outlined-button
                                class="column-language-button"
                                data-testid="column-language-toggle"
                                  @click=${(e: Event) => {
                                    e.stopPropagation()
                                    this.toggleColumnDropdown(colIndex)
                                  }}
                                >
                                  <span>${colLang ? t(colLang.name) : col.languageId}</span>
                                  <span>${this.columnDropdownOpen[colIndex] ? '▲' : '▼'}</span>
                                </md-outlined-button>
                                <div class="column-language-dropdown ${this.columnDropdownOpen[colIndex] ? 'open' : ''}" data-testid="column-language-dropdown">
                                  ${this.dataManager.getLanguages().map(lang => html`
                                    <div
                                      class="column-language-option"
                                      @click=${(e: Event) => {
                                        e.stopPropagation()
                                        this.changeColumnLanguage(colIndex, lang.id)
                                      }}
                                    >
                                      ${t(lang.name)}
                                    </div>
                                  `)}
                                </div>
                              </div>
                              ${this.dataManager.getMeaningTypes().length > 0 ? html`
                                <div class="column-language-select" data-column-index="${colIndex}">
                                  <md-outlined-button
                                    class="column-language-button"
                                    data-testid="column-meaning-type-toggle"
                                    @click=${(e: Event) => {
                                      e.stopPropagation()
                                      this.toggleColumnMeaningTypeDropdown(colIndex)
                                    }}
                                  >
                                    <span>
                                      ${col.meaningTypeIds.length === 0
                                        ? t('wordView.allMeaningTypes') || 'All types'
                                        : col.meaningTypeIds.length === 1
                                          ? t(this.dataManager.getMeaningTypes().find(mt => mt.id === col.meaningTypeIds[0])?.name || '')
                                          : t('wordView.selectedMeaningTypes', { count: String(col.meaningTypeIds.length) })
                                      }
                                    </span>
                                    <span>${this.columnMeaningTypeDropdownOpen[colIndex] ? '▲' : '▼'}</span>
                                  </md-outlined-button>
                                  <div class="column-language-dropdown ${this.columnMeaningTypeDropdownOpen[colIndex] ? 'open' : ''}" data-testid="column-meaning-type-dropdown">
                                    ${this.dataManager.getMeaningTypes().map(mt => {
                                      const isSelected = col.meaningTypeIds.includes(mt.id)
                                      return html`
                                        <div
                                          class="column-language-option"
                                          @click=${(e: Event) => {
                                            e.stopPropagation()
                                            this.toggleColumnMeaningType(colIndex, mt.id)
                                          }}
                                          style="display: flex; align-items: center; gap: 0.5rem;"
                                        >
                                          <div class="multi-select-checkbox ${isSelected ? 'checked' : ''}" style="width: 0.75rem; height: 0.75rem;"></div>
                                          <span>${t(mt.name)}</span>
                                        </div>
                                      `
                                    })}
                                  </div>
                                </div>
                              ` : ''}
                              ${(() => {
                                const availableSystems = this.getAvailableSpellingSystemsForLanguage(col.languageId)
                                if (availableSystems.length > 1) {
                                  return html`
                                    <div class="column-language-select" data-column-index="${colIndex}">
                                      <md-outlined-button
                                        class="column-language-button"
                                        data-testid="column-spelling-system-toggle"
                                        @click=${(e: Event) => {
                                          e.stopPropagation()
                                          this.toggleColumnSpellingSystemDropdown(colIndex)
                                        }}
                                      >
                                        <span>
                                          ${col.spellingSystemIds.length === 0
                                            ? t('wordView.allSpellingSystems') || 'All systems'
                                            : col.spellingSystemIds.length === 1
                                              ? t(availableSystems.find(s => s.id === col.spellingSystemIds[0])?.name || '')
                                              : t('wordView.selectedSpellingSystems', { count: String(col.spellingSystemIds.length) })
                                          }
                                        </span>
                                        <span>${this.columnSpellingSystemDropdownOpen[colIndex] ? '▲' : '▼'}</span>
                                      </md-outlined-button>
                                      <div class="column-language-dropdown ${this.columnSpellingSystemDropdownOpen[colIndex] ? 'open' : ''}" data-testid="column-spelling-system-dropdown">
                                        ${availableSystems.map(system => {
                                          const isSelected = col.spellingSystemIds.includes(system.id)
                                          return html`
                                            <div
                                              class="column-language-option"
                                              @click=${(e: Event) => {
                                                e.stopPropagation()
                                                this.toggleColumnSpellingSystem(colIndex, system.id)
                                              }}
                                              style="display: flex; align-items: center; gap: 0.5rem;"
                                            >
                                              <div class="multi-select-checkbox ${isSelected ? 'checked' : ''}" style="width: 0.75rem; height: 0.75rem;"></div>
                                              <span>${t(system.name)}</span>
                                            </div>
                                          `
                                        })}
                                      </div>
                                    </div>
                                  `
                                }
                                return html``
                              })()}
                            </div>
                            ${this.columns.length > 1 ? html`
                              <md-outlined-button
                                @click=${() => this.removeColumn(colIndex)}
                                data-testid="remove-column"
                                style="min-width: auto; padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                              >
                                ×
                              </md-outlined-button>
                            ` : ''}
                          </div>
                        </div>
                      `
                    })}
                    ${this.dataManager.getLanguages().some(lang => !this.columns.some(col => col.languageId === lang.id)) ? html`
                      <div class="column">
                        <md-outlined-button
                          @click=${this.addColumn}
                          data-testid="add-column"
                        >
                          + ${t('wordView.addColumn')}
                        </md-outlined-button>
                      </div>
                    ` : ''}
                    </div>
                    <!-- Content rows (shared component) -->
                    <word-entries-list
                      .groups=${list}
                      .columns=${this.columns}
                      .languages=${this.dataManager.getLanguages()}
                      .meaningTypes=${this.dataManager.getMeaningTypes()}
                      .showHeader=${false}
                    ></word-entries-list>
                  </div>
                  ${showPagination ? this.renderPagination() : ''}
                `}
      </div>
    `
  }

  private renderPagination() {
    const total = this.dataManager.getTotalCount() // Use server's total count (words when filtered)
    const pages = this.dataManager.getTotalPages()
    const current = this.currentPage

    // Calculate which page numbers to show (max 7 page buttons)
    const getPageNumbers = (): number[] => {
      if (pages <= 7) {
        return Array.from({ length: pages }, (_, i) => i + 1)
      }
      
      if (current <= 4) {
        return [1, 2, 3, 4, 5, 6, 7]
      }
      
      if (current >= pages - 3) {
        return [pages - 6, pages - 5, pages - 4, pages - 3, pages - 2, pages - 1, pages]
      }
      
      return [current - 3, current - 2, current - 1, current, current + 1, current + 2, current + 3]
    }

    const pageNumbers = getPageNumbers()

    return html`
      <div class="pagination">
        <md-outlined-button
          class="pagination-button"
          @click=${this.previousPage}
          ?disabled=${current === 1}
        >
          ${t('wordView.pagination.previous')}
        </md-outlined-button>

        <div class="pagination-page-numbers">
          ${pageNumbers[0] > 1 ? html`
            <md-outlined-button
              class="pagination-page-button"
              @click=${() => this.goToPage(1)}
            >
              1
            </md-outlined-button>
            ${pageNumbers[0] > 2 ? html`<span style="padding: 0 0.25rem;">…</span>` : ''}
          ` : ''}
          
          ${pageNumbers.map((page) => {
            const isActive = page === current
            return isActive
              ? html`
                  <md-filled-button
                    class="pagination-page-button active"
                    @click=${() => this.goToPage(page)}
                  >
                    ${page}
                  </md-filled-button>
                `
              : html`
                  <md-outlined-button
                    class="pagination-page-button"
                    @click=${() => this.goToPage(page)}
                  >
                    ${page}
                  </md-outlined-button>
                `
          })}
          
          ${pageNumbers[pageNumbers.length - 1] < pages ? html`
            ${pageNumbers[pageNumbers.length - 1] < pages - 1 ? html`<span style="padding: 0 0.25rem;">…</span>` : ''}
            <md-outlined-button
              class="pagination-page-button"
              @click=${() => this.goToPage(pages)}
            >
              ${pages}
            </md-outlined-button>
          ` : ''}
        </div>

        <md-outlined-button
          class="pagination-button"
          @click=${this.nextPage}
          ?disabled=${current === pages}
        >
          ${t('wordView.pagination.next')}
        </md-outlined-button>

        <div class="pagination-info">
          ${t('wordView.pagination.info', {
            start: String(this.pageStart),
            end: String(this.pageEnd),
            total: String(total)
          })}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'word-view-page': WordViewPage
  }
}
